import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { api } from '../utils/api';
import {
  JOURS_COURTS, MOIS, getMonday, formatDate, formatDateISO,
  addWeeks, getWeekDays, getWeekOffset, getWeekLabel,
  getMonthsInWeek, generateTimeSlots, getISOWeekNumber
} from '../utils/dates';
import {
  ObjectivesModal,
  SlotModal, NoteModal, DuplicateModal, ClearModal
} from '../components/AdminPanels';
import PlanningModernStoreView from '../components/PlanningModernStoreView';
import { isModernPlanningStore } from '../utils/planningStores';

export default function SchedulePage({ onStoreChange, onOpenPlanningHelp }) {
  const { user, canViewStore, canManageStore } = useAuth();
  const toast = useToast();

  const [stores, setStores] = useState([]);
  const [currentStore, setCurrentStore] = useState(null);
  const canManagePlanning = Boolean(currentStore && canManageStore('manage_planning', currentStore.id));
  const canManageObjectives = Boolean(currentStore && canManageStore('manage_objectives', currentStore.id));
  const isLaure = String(user?.username || '').trim().toLowerCase() === 'laure';
  const [storeUsers, setStoreUsers] = useState([]);
  const [currentMonday, setCurrentMonday] = useState(getMonday(new Date()));
  const [slots, setSlots] = useState([]);
  const [notes, setNotes] = useState([]);
  const [hours, setHours] = useState([]);
  const [monthlyHours, setMonthlyHours] = useState([]);
  const [objectives, setObjectives] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showObjectives, setShowObjectives] = useState(false);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(null); // 'day' | 'week'
  const [showClear, setShowClear] = useState(null); // 'day' | 'week'
  const [showObjPopup, setShowObjPopup] = useState(false); // mobile objectives popup
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayDate, setHolidayDate] = useState('');
  
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [duplicateSource, setDuplicateSource] = useState('');

  // Admin menus
  const [adminMenu2Open, setAdminMenu2Open] = useState(false);
  const adminRef2 = useRef(null);

  // Objective month for editing
  const [objMonth, setObjMonth] = useState(new Date().getMonth() + 1);
  const [objYear, setObjYear] = useState(new Date().getFullYear());

  // Close admin menus on outside click
  useEffect(() => {
    const handler = (e) => {
      if (adminRef2.current && !adminRef2.current.contains(e.target)) setAdminMenu2Open(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load stores
  useEffect(() => { loadStores(); }, []);

  const loadStores = async () => {
    try {
      const data = (await api.getStores()).filter(store => canViewStore('planning', store.id));
      setStores(data);
      if (data.length > 0) {
        const savedId = parseInt(localStorage.getItem('currentStoreId'));
        const saved = savedId ? data.find(s => s.id === savedId) : null;
        setCurrentStore(previous => data.find(s => s.id === previous?.id) || saved || data[0]);
      } else {
        setCurrentStore(null);
      }
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleStoreChange = (store) => {
    setCurrentStore(store);
    localStorage.setItem('currentStoreId', store.id);
  };

  useEffect(() => {
    onStoreChange?.(currentStore);
  }, [currentStore, onStoreChange]);

  // Load data when store or week changes
  useEffect(() => {
    if (currentStore) loadWeekData();
  }, [currentStore, currentMonday]);

  const loadWeekData = useCallback(async () => {
    if (!currentStore) return;
    setLoading(true);
    const start = formatDateISO(currentMonday);
    const end = formatDateISO(addWeeks(currentMonday, 0).getTime() + 6 * 86400000);
    
    try {
      const [slotsData, notesData, hoursData, usersData, holidaysData] = await Promise.all([
        api.getSlots(currentStore.id, start, end),
        api.getNotes(currentStore.id, start, end),
        api.getHours(currentStore.id, start, end),
        api.getStoreUsers(currentStore.id),
        api.getHolidays(currentStore.id, start, end),
      ]);
      setSlots(slotsData);
      setNotes(notesData);
      setHours(hoursData);
      setStoreUsers(usersData);
      setHolidays(holidaysData);

      // Load objectives for months in this week
      const months = getMonthsInWeek(currentMonday);
      const objData = {};
      for (const m of months) {
        const obj = await api.getObjectives(currentStore.id, m.month, m.year);
        objData[`${m.year}-${m.month}`] = obj;
      }
      setObjectives(objData);

      // Load monthly hours for admin
      if (canManagePlanning) {
        const monthStart = new Date(currentMonday.getFullYear(), currentMonday.getMonth(), 1);
        const monthEnd = new Date(currentMonday.getFullYear(), currentMonday.getMonth() + 1, 0);
        const monthHoursData = await api.getHours(currentStore.id, formatDateISO(monthStart), formatDateISO(monthEnd));
        setMonthlyHours(monthHoursData);
      }
    } catch (e) { toast(e.message, 'error'); }
    setLoading(false);
  }, [currentStore, currentMonday, toast, canManagePlanning]);

  const weekDays = getWeekDays(currentMonday);
  const weekOffset = getWeekOffset(currentMonday);
  const weekLabel = getWeekLabel(weekOffset);
  const weekNum = getISOWeekNumber(currentMonday);
  const monthsInWeek = getMonthsInWeek(currentMonday);
  const startISO = formatDateISO(currentMonday);
  const endISO = formatDateISO(weekDays[6]);

  const timeSlots = currentStore
    ? generateTimeSlots(currentStore.opening_time || '09:00', currentStore.closing_time || '19:00')
    : generateTimeSlots();

  // Convert "HH:MM" to minutes
  const timeToMin = (t) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  // Get slots for a specific cell - always sorted alphabetically
  const getSlotsForCell = (date, timeStart) => {
    const dateStr = formatDateISO(date);
    const cellMin = timeToMin(timeStart);
    return slots.filter(s => {
      if (s.date !== dateStr) return false;
      const sStart = timeToMin(s.start_time);
      const sEnd = timeToMin(s.end_time);
      return sStart <= cellMin && sEnd > cellMin;
    }).sort((a, b) => a.username.localeCompare(b.username));
  };

  // Get all unique users for a given day, sorted alphabetically
  // Used to keep consistent left/right columns across all time slots
  const getDayUsers = (dateStr) => {
    const daySlots = slots.filter(s => s.date === dateStr);
    const userMap = new Map();
    daySlots.forEach(s => {
      if (!userMap.has(s.username)) userMap.set(s.username, s);
    });
    return [...userMap.keys()].sort((a, b) => a.localeCompare(b));
  };

  // Handle cell click
  const handleCellClick = (date, timeStart) => {
    if (!canManagePlanning) return;
    const dateStr = formatDateISO(date);
    if (holidays.some(h => h.date === dateStr)) return; // blocked on holidays
    const cellSlots = getSlotsForCell(date, timeStart);
    if (cellSlots.length === 1) {
      setSelectedSlot(cellSlots[0]);
      setSelectedDate(null);
      setSelectedTime(null);
    } else if (cellSlots.length === 0) {
      setSelectedSlot(null);
      setSelectedDate(formatDateISO(date));
      setSelectedTime(timeStart);
    } else {
      // Multiple: open first for editing
      setSelectedSlot(cellSlots[0]);
      setSelectedDate(null);
      setSelectedTime(null);
    }
    setShowSlotModal(true);
  };

  // Holiday management
  const isHoliday = (dateStr) => holidays.find(h => h.date === dateStr);

  const handleAddHoliday = async () => {
    if (!holidayDate) return toast('Sélectionnez une date', 'error');
    const d = new Date(holidayDate);
    if (d.getDay() === 0) return toast('Le dimanche est déjà non ouvré', 'error');
    try {
      await api.createHoliday({ store_id: currentStore.id, date: holidayDate });
      toast('Jour férié ajouté', 'success');
      setShowHolidayModal(false);
      setHolidayDate('');
      loadWeekData();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleRemoveHoliday = async (holiday) => {
    if (!window.confirm(`Supprimer le jour férié du ${formatDate(new Date(holiday.date))} ?`)) return;
    try {
      await api.deleteHoliday(holiday.id);
      toast('Jour férié supprimé', 'success');
      loadWeekData();
    } catch (e) { toast(e.message, 'error'); }
  };

  // PDF Export
  const handleExportPDF = async () => {
    try {
      const jsPDF = (await import('jspdf')).default;
      await import('jspdf-autotable');
      const doc = new jsPDF('landscape');
      
      doc.setFontSize(16);
      doc.text(`Planning - ${currentStore.name}`, 14, 15);
      doc.setFontSize(10);
      doc.text(`${weekLabel} (S${weekNum}) - ${formatDate(currentMonday)} au ${formatDate(weekDays[6])}`, 14, 23);

      const headers = ['Heure', ...weekDays.map((d, i) => `${JOURS_COURTS[i]} ${formatDate(d)}`)];
      const rows = timeSlots.map(ts => {
        const row = [ts.start];
        weekDays.forEach(day => {
          const cellSlots = getSlotsForCell(day, ts.start);
          row.push(cellSlots.map(s => {
            const st = s.status === 'present' ? '✓' : s.status === 'absent' ? '✗' : '◎';
            return `${st} ${s.username}`;
          }).join(' / ') || '');
        });
        return row;
      });

      doc.autoTable({ head: [headers], body: rows, startY: 28, styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [79, 143, 255] } });
      doc.save(`planning-${currentStore.name}-S${weekNum}.pdf`);
      toast('PDF exporté', 'success');
    } catch (e) { toast('Erreur export PDF', 'error'); }
  };

  // Render objectives section
  const renderObjectives = () => {
    return monthsInWeek.map(m => {
      const key = `${m.year}-${m.month}`;
      const objs = objectives[key] || [];
      // Filter: only show lines that have a value
      const visibleObjs = objs.filter(o => o.value_text && o.value_text.trim() !== '');
      return (
        <div key={key} className="objectives-container">
          <div className="objectives-title">
            🎯 Objectifs {m.name} {m.year}
            {canManageObjectives && (
              <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
                onClick={() => { setObjMonth(m.month); setObjYear(m.year); setShowObjectives(true); }}>
                Modifier
              </button>
            )}
          </div>
          {visibleObjs.length > 0 ? (
            <div className="objectives-grid">
              {visibleObjs.map((o, i) => (
                <div key={i} className="objective-row">
                  <div className="objective-label">{o.objective_text || `Objectif ${i + 1}`}</div>
                  <div className="objective-value">{o.value_text}{(o.line_order <= 2) ? ' €' : ''}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="objectives-empty">Objectifs non définis</div>
          )}
        </div>
      );
    });
  };

  const todayISO = formatDateISO(new Date());
  const isModernStore = isModernPlanningStore(currentStore);

  if (isModernStore) {
    return <>
      <PlanningModernStoreView
        stores={stores} currentStore={currentStore} onStoreChange={handleStoreChange}
        currentMonday={currentMonday} setCurrentMonday={setCurrentMonday}
        slots={slots} notes={notes} weeklyHours={hours} monthlyHours={monthlyHours}
        objectives={objectives} holidays={holidays} users={storeUsers} loading={loading}
        canManagePlanning={canManagePlanning} canManageObjectives={canManageObjectives}
        onAddSlot={(date,time)=>{ setSelectedSlot(null); setSelectedDate(date); setSelectedTime(time); setShowSlotModal(true); }}
        onEditSlot={slot=>{ setSelectedSlot(slot); setSelectedDate(null); setSelectedTime(null); setShowSlotModal(true); }}
        onAddNote={date=>{ setSelectedNote(null); setSelectedDate(date); setShowNoteModal(true); }}
        onEditNote={note=>{ setSelectedNote(note); setSelectedDate(note.date); setShowNoteModal(true); }}
        onDuplicate={setShowDuplicate} onClear={setShowClear}
        onAddHoliday={()=>{ setHolidayDate(''); setShowHolidayModal(true); }} onRemoveHoliday={handleRemoveHoliday}
        onEditObjectives={(month,year)=>{ setObjMonth(month); setObjYear(year); setShowObjectives(true); }}
      />
      {canManageObjectives && <ObjectivesModal isOpen={showObjectives} onClose={()=>setShowObjectives(false)} storeId={currentStore.id} month={objMonth} year={objYear} onRefresh={loadWeekData}/>}
      {canManagePlanning && <>
        <SlotModal isOpen={showSlotModal} onClose={()=>{setShowSlotModal(false);setSelectedSlot(null);}} storeId={currentStore.id} storeUsers={storeUsers} defaultDate={selectedDate} defaultTime={selectedTime} editSlot={selectedSlot} onRefresh={loadWeekData}/>
        <NoteModal isOpen={showNoteModal} onClose={()=>{setShowNoteModal(false);setSelectedNote(null);}} storeId={currentStore.id} date={selectedDate} editNote={selectedNote} onRefresh={loadWeekData}/>
        <DuplicateModal isOpen={!!showDuplicate} onClose={()=>setShowDuplicate(null)} type={showDuplicate} storeId={currentStore.id} weekStart={startISO} weekEnd={endISO} onRefresh={loadWeekData}/>
        <ClearModal isOpen={!!showClear} onClose={()=>setShowClear(null)} type={showClear} storeId={currentStore.id} storeUsers={storeUsers} date={startISO} weekStart={startISO} weekEnd={endISO} onRefresh={loadWeekData}/>
      </>}
      {showHolidayModal && <div className="modal-overlay" onClick={()=>setShowHolidayModal(false)}><div className="modal-content" onClick={event=>event.stopPropagation()} style={{maxWidth:400}}><div className="modal-header"><h2>🎌 Ajouter un jour férié</h2><button className="modal-close" onClick={()=>setShowHolidayModal(false)}>&times;</button></div><div style={{padding:20}}><p style={{fontSize:'.85rem',color:'var(--text-muted)',marginBottom:16}}>Le jour sélectionné sera marqué comme férié pour <strong>{currentStore.name}</strong>.</p><div className="form-group"><label>Date</label><input className="form-input" type="date" value={holidayDate} onChange={event=>setHolidayDate(event.target.value)}/></div><div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:18}}><button className="btn btn-secondary" onClick={()=>setShowHolidayModal(false)}>Annuler</button><button className="btn btn-primary" onClick={handleAddHoliday} disabled={!holidayDate}>Ajouter</button></div></div></div></div>}
    </>;
  }

  return (
    <div className="main-content">
      {/* Store selector */}
      <div className="store-bar">
        {stores.map(s => (
          <button key={s.id}
            className={`store-tab ${currentStore?.id === s.id ? 'active' : ''}`}
            onClick={() => handleStoreChange(s)}>
            <span className="store-tab-dot" style={{ background: s.color }} />
            {s.name}
          </button>
        ))}
      </div>

      {/* Week navigation */}
      <div className="week-nav">
        <div className="week-nav-center">
          <button className="btn btn-secondary btn-sm" onClick={() => setCurrentMonday(addWeeks(currentMonday, -1))}>← Préc.</button>
          <div>
            <div className="week-label">{weekLabel} (S{weekNum})</div>
            <div className="week-dates">{formatDate(currentMonday)} → {formatDate(weekDays[6])}</div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setCurrentMonday(addWeeks(currentMonday, 1))}>Suiv. →</button>
          {weekOffset !== 0 && (
            <button className="btn btn-secondary btn-sm" onClick={() => setCurrentMonday(getMonday(new Date()))}>Aujourd'hui</button>
          )}
        </div>

        <div className="week-nav-actions">
          {canManagePlanning && (
            <div className="admin-menu" ref={adminRef2}>
              <button className="btn btn-primary btn-sm" onClick={() => { setAdminMenu2Open(!adminMenu2Open); }}>
                📋 Gestion des horaires
              </button>
              {adminMenu2Open && (
                <div className="admin-menu-dropdown">
                  <button className="admin-menu-item" onClick={() => { setSelectedSlot(null); setSelectedDate(startISO); setSelectedTime(currentStore?.opening_time || '09:00'); setShowSlotModal(true); setAdminMenu2Open(false); }}>➕ Ajouter un créneau</button>
                  <div className="admin-menu-divider" />
                  <button className="admin-menu-item" onClick={() => { setShowDuplicate('day'); setAdminMenu2Open(false); }}>📋 Dupliquer une journée</button>
                  <button className="admin-menu-item" onClick={() => { setShowDuplicate('week'); setAdminMenu2Open(false); }}>📋 Dupliquer la semaine</button>
                  <div className="admin-menu-divider" />
                  <button className="admin-menu-item" onClick={() => { setShowClear('day'); setAdminMenu2Open(false); }}>🗑️ Effacer une journée</button>
                  <button className="admin-menu-item" onClick={() => { setShowClear('week'); setAdminMenu2Open(false); }}>🗑️ Effacer la semaine</button>
                  <div className="admin-menu-divider" />
                  <button className="admin-menu-item" onClick={() => { setShowHolidayModal(true); setHolidayDate(''); setAdminMenu2Open(false); }}>🎌 Jour férié</button>
                  <div className="admin-menu-divider" />
                  <button className="admin-menu-item" onClick={() => { setSelectedNote(null); setSelectedDate(todayISO); setShowNoteModal(true); setAdminMenu2Open(false); }}>📝 Ajouter une note</button>
                  {canManageObjectives && (
                    <button className="admin-menu-item" onClick={() => { setObjMonth(new Date().getMonth() + 1); setObjYear(new Date().getFullYear()); setShowObjectives(true); setAdminMenu2Open(false); }}>🎯 Objectifs mensuels</button>
                  )}
                  {isLaure && <div className="admin-menu-divider" />}
                  {isLaure && <button className="admin-menu-item" onClick={() => { setAdminMenu2Open(false); onOpenPlanningHelp?.(); }}>Aide Planning</button>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Users legend + PDF */}
      <div className="users-legend">
        {storeUsers.filter(u => u.visible_planning !== 0).map(u => (
          <div key={u.id} className="user-legend-item">
            <div className="user-legend-dot" style={{ background: u.color }} />
            {u.username}
          </div>
        ))}
        <button className="btn btn-secondary btn-sm legend-pdf-btn" onClick={handleExportPDF}>📄 PDF</button>
      </div>

      {/* Schedule grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }} className="loading-pulse">
          Chargement du planning...
        </div>
      ) : (
        <div className="schedule-container">
          <div className="schedule-grid">
            {/* Header row - jour et date sur 1 ligne */}
            <div className="schedule-header-cell" />
            {weekDays.map((day, i) => {
              const isToday = formatDateISO(day) === todayISO;
              const hol = isHoliday(formatDateISO(day));
              return (
                <div key={i} className={`schedule-header-cell ${isToday ? 'schedule-day-today' : ''} ${hol ? 'schedule-day-holiday' : ''} ${i % 2 === 0 ? 'day-even' : 'day-odd'}`}>
                  <div className="schedule-day-inline">
                    <span className="schedule-day-name">{JOURS_COURTS[i]}</span>
                    <span className="schedule-day-date">{formatDate(day)}</span>
                    {hol && <span className="schedule-holiday-badge">🎌 FÉRIÉ</span>}
                    {hol && canManagePlanning && (
                      <button className="schedule-holiday-remove" onClick={(e) => { e.stopPropagation(); handleRemoveHoliday(hol); }} title="Supprimer le jour férié">✕</button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Notes row - sous les dates */}
            <div className="schedule-note-label">📝</div>
            {weekDays.map((day, i) => {
              const dayNotes = notes.filter(n => n.date === formatDateISO(day));
              const noteHoliday = !!isHoliday(formatDateISO(day));
              return (
                <div key={`note-${i}`} className={`schedule-note-cell ${i % 2 === 0 ? 'day-even' : 'day-odd'} ${noteHoliday ? 'cell-holiday' : ''}`}
                  onClick={() => { if (canManagePlanning && !noteHoliday) { setSelectedNote(null); setSelectedDate(formatDateISO(day)); setShowNoteModal(true); } }}>
                  {noteHoliday ? (
                    <div className="schedule-note-empty" style={{ color: '#991b1b', fontWeight: 600, fontSize: '0.7rem' }}>FÉRIÉ</div>
                  ) : dayNotes.length > 0 ? (
                    dayNotes.map(n => (
                      <div key={n.id} className="schedule-note-item" onClick={(e) => {
                        if (canManagePlanning) { e.stopPropagation(); setSelectedNote(n); setSelectedDate(n.date); setShowNoteModal(true); }
                      }}>
                        {n.content}
                      </div>
                    ))
                  ) : (
                    <div className="schedule-note-empty">{canManagePlanning ? '+' : '—'}</div>
                  )}
                </div>
              );
            })}

            {/* Time rows */}
            {timeSlots.map((ts, ri) => {
              const isHourMark = ts.start.endsWith(':00');
              return (
                <React.Fragment key={ri}>
                  <div className={`schedule-time-cell ${isHourMark ? 'hour-mark' : ''}`}>
                    {ts.start}
                  </div>
                  {weekDays.map((day, di) => {
                    const dateStr = formatDateISO(day);
                    const dayIsHoliday = !!isHoliday(dateStr);
                    const cellSlots = getSlotsForCell(day, ts.start);
                    
                    // Get ALL users who work this day (for consistent columns)
                    const dayUsers = getDayUsers(dateStr);
                    const hasTwoColumns = dayUsers.length >= 2;
                    
                    return (
                      <div key={di}
                        className={`schedule-slot-cell ${isHourMark ? 'hour-mark' : ''} ${cellSlots.length > 0 ? 'has-slots' : ''} ${di % 2 === 0 ? 'day-even' : 'day-odd'} ${dayIsHoliday ? 'cell-holiday' : ''}`}
                        onClick={() => handleCellClick(day, ts.start)}>
                        {hasTwoColumns ? (
                          <div className="slot-cell-content slot-split">
                            {dayUsers.slice(0, 2).map((userName) => {
                              const s = cellSlots.find(sl => sl.username === userName);
                              if (!s) {
                                // Empty column placeholder
                                return <div key={userName} className="slot-fill slot-fill-empty" />;
                              }
                              const sStartMin = timeToMin(s.start_time);
                              const cellMin = timeToMin(ts.start);
                              const cellIndex = (cellMin - sStartMin) / 30;
                              const showName = cellIndex === 0 || cellIndex % 3 === 0;
                              
                              const bgColor = s.status === 'present' 
                                ? `${s.user_color}35` 
                                : 'rgba(140,149,166,0.15)';
                              const borderColor = s.status === 'present'
                                ? s.user_color
                                : s.status === 'absent' ? '#dc2626' : '#d4940a';
                              
                              return (
                                <div key={s.id}
                                  className={`slot-fill status-${s.status}`}
                                  style={{ backgroundColor: bgColor, borderLeftColor: borderColor }}
                                  onClick={(e) => { e.stopPropagation(); if (canManagePlanning) { setSelectedSlot(s); setSelectedDate(null); setSelectedTime(null); setShowSlotModal(true); } }}>
                                  {showName && (
                                    <span className="slot-fill-name">
                                      <span className="slot-fill-icon">
                                        {s.status === 'present' ? '✅' : s.status === 'absent' ? '❌' : '🏖️'}
                                      </span>
                                      {s.username}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : cellSlots.length === 1 ? (
                          <div className="slot-cell-content">
                            {cellSlots.map((s) => {
                              const sStartMin = timeToMin(s.start_time);
                              const cellMin = timeToMin(ts.start);
                              const cellIndex = (cellMin - sStartMin) / 30;
                              const showName = cellIndex === 0 || cellIndex % 3 === 0;
                              
                              const bgColor = s.status === 'present' 
                                ? `${s.user_color}35` 
                                : 'rgba(140,149,166,0.15)';
                              const borderColor = s.status === 'present'
                                ? s.user_color
                                : s.status === 'absent' ? '#dc2626' : '#d4940a';
                              
                              return (
                                <div key={s.id}
                                  className={`slot-fill status-${s.status}`}
                                  style={{ backgroundColor: bgColor, borderLeftColor: borderColor }}
                                  onClick={(e) => { e.stopPropagation(); if (canManagePlanning) { setSelectedSlot(s); setSelectedDate(null); setSelectedTime(null); setShowSlotModal(true); } }}>
                                  {showName && (
                                    <span className="slot-fill-name">
                                      <span className="slot-fill-icon">
                                        {s.status === 'present' ? '✅' : s.status === 'absent' ? '❌' : '🏖️'}
                                      </span>
                                      {s.username}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Hour counters - admin only */}
      {canManagePlanning && (
        <div className="hours-admin-section">
          <div className="hours-block">
            <div className="hours-block-title">📊 Heures semaine (S{weekNum})</div>
            <div className="hours-section">
              {hours.map(h => (
                <div key={h.user_id} className="hour-counter">
                  <div className="hour-counter-dot" style={{ background: h.color }} />
                  <span className="hour-counter-name">{h.username}</span>
                  <span className="hour-counter-value">{Number(h.total_hours).toFixed(1)}h</span>
                </div>
              ))}
              {hours.length === 0 && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Aucune heure cette semaine</span>}
            </div>
          </div>
          <div className="hours-block">
            <div className="hours-block-title">📅 Heures mois ({MOIS[currentMonday.getMonth()]})</div>
            <div className="hours-section">
              {monthlyHours.map(h => (
                <div key={h.user_id} className="hour-counter">
                  <div className="hour-counter-dot" style={{ background: h.color }} />
                  <span className="hour-counter-name">{h.username}</span>
                  <span className="hour-counter-value">{Number(h.total_hours).toFixed(1)}h</span>
                </div>
              ))}
              {monthlyHours.length === 0 && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Aucune heure ce mois</span>}
            </div>
          </div>
        </div>
      )}

      {/* Objectives - Floating window (desktop) / FAB popup (mobile) */}
      <button className="objectives-fab" onClick={() => setShowObjPopup(!showObjPopup)}>
        {showObjPopup ? '✕' : '🎯'}
      </button>

      {/* Desktop: floating window next to agenda */}
      {showObjPopup && (
        <div className="objectives-floating">
          <div className="objectives-floating-header">
            <h3>🎯 Objectifs</h3>
            <button className="objectives-floating-close" onClick={() => setShowObjPopup(false)}>&times;</button>
          </div>
          <div className="objectives-floating-body">
            {renderObjectives()}
          </div>
        </div>
      )}

      {/* Mobile: full-screen popup (triggered by same FAB button) */}
      {showObjPopup && (
        <div className="mobile-obj-popup modal-overlay" onClick={() => setShowObjPopup(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🎯 Objectifs</h2>
              <button className="modal-close" onClick={() => setShowObjPopup(false)}>&times;</button>
            </div>
            {renderObjectives()}
          </div>
        </div>
      )}

      {/* All modals */}
      {canManageObjectives && (
        <ObjectivesModal isOpen={showObjectives} onClose={() => setShowObjectives(false)} storeId={currentStore?.id} month={objMonth} year={objYear} onRefresh={loadWeekData} />
      )}
      {canManagePlanning && (
        <>
          <SlotModal isOpen={showSlotModal} onClose={() => { setShowSlotModal(false); setSelectedSlot(null); }} storeId={currentStore?.id} storeUsers={storeUsers} defaultDate={selectedDate} defaultTime={selectedTime} editSlot={selectedSlot} onRefresh={loadWeekData} />
          <NoteModal isOpen={showNoteModal} onClose={() => { setShowNoteModal(false); setSelectedNote(null); }} storeId={currentStore?.id} date={selectedDate} editNote={selectedNote} onRefresh={loadWeekData} />
          <DuplicateModal isOpen={!!showDuplicate} onClose={() => setShowDuplicate(null)} type={showDuplicate} storeId={currentStore?.id} weekStart={startISO} weekEnd={endISO} onRefresh={loadWeekData} />
          <ClearModal isOpen={!!showClear} onClose={() => setShowClear(null)} type={showClear} storeId={currentStore?.id} storeUsers={storeUsers} date={startISO} weekStart={startISO} weekEnd={endISO} onRefresh={loadWeekData} />
        </>
      )}

      {/* Holiday modal */}
      {showHolidayModal && (
        <div className="modal-overlay" onClick={() => setShowHolidayModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>🎌 Ajouter un jour férié</h2>
              <button className="modal-close" onClick={() => setShowHolidayModal(false)}>&times;</button>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 16 }}>
                Le jour sélectionné sera marqué comme férié pour <strong>{currentStore?.name}</strong>. Aucun créneau ne pourra y être planifié et il sera exclu des jours ouvrés dans les statistiques.
              </p>
              <div className="form-group">
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Date</label>
                <input type="date" value={holidayDate} onChange={e => setHolidayDate(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.92rem', fontFamily: 'var(--font-body)', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
                <button className="btn btn-secondary" onClick={() => setShowHolidayModal(false)}>Annuler</button>
                <button className="btn btn-primary" onClick={handleAddHoliday} disabled={!holidayDate}>Ajouter</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
